// ─── SignalR Connection Manager ───
// Manages a singleton Azure SignalR connection.
// Graceful degradation: if the negotiate endpoint is unavailable or the
// connection fails, the app continues to work via REST polling.

// ⚠️ TYPE-ONLY on purpose — do not add a value import of '@microsoft/signalr' here.
// A static value import makes the package reachable from the EAGER module graph, so Rollup
// emits it as a modulepreload in index.html and the browser fetches ~14 KB gz before first
// paint — for a transport that cannot be used until after the app has mounted and negotiated.
// Splitting it into its own `manualChunks` entry does NOT prevent that: manual chunks control
// which FILE code lands in, never WHEN it loads. Only the dynamic import in
// `establishConnection()` keeps it off the critical path. See the perf budget in
// `apps/web/perf-budget.json` (`initialJsGzipKB`).
import type { HubConnection, IRetryPolicy, RetryContext } from '@microsoft/signalr';

const SIGNALR_NEGOTIATE_URL = `${import.meta.env.VITE_API_BASE_URL ?? '/api/v1'}/signalr/negotiate`;

/**
 * The one backoff curve for everything SignalR retries: 1s · 2ⁿ, capped at 30s, never null.
 *
 * Exported because there are two retry surfaces that must not diverge (#330): the
 * `withAutomaticReconnect` policy below covers a connection that OPENED and then dropped, and
 * `useSignalR` covers the case that policy structurally cannot see — an initial `start()` that
 * never succeeded, which `@microsoft/signalr` hands back to the caller with no retry at all.
 */
export function retryDelayMs(previousRetryCount: number): number {
  return Math.min(30_000, 1_000 * 2 ** previousRetryCount);
}

/**
 * Retry for as long as the tab is open, backing off to a 30s ceiling (#322).
 *
 * `withAutomaticReconnect()` with no argument uses SignalR's default policy, whose delay table
 * is `[0, 2000, 10000, 30000, null]` — the trailing `null` means STOP. That abandons the
 * connection permanently about 42 seconds after a drop, so a closed laptop lid, a wifi handover
 * or any outage longer than that left real-time dead until the user reloaded the page, showing
 * the same "no live updates" banner as #310 from an unrelated cause.
 *
 * Never returning null means we never give up. That is safe here because the hook tears the
 * connection down when the user signs out, and each attempt is one cheap request against an
 * app that is already polling on the same interval.
 */
const RECONNECT_POLICY: IRetryPolicy = {
  nextRetryDelayInMilliseconds(ctx: RetryContext): number {
    return retryDelayMs(ctx.previousRetryCount);
  },
};

let connection: HubConnection | null = null;
let inFlight: Promise<HubConnection | null> | null = null;

interface NegotiateResponse {
  url: string;
  accessToken: string;
}

/**
 * Unwrap the negotiate body (#310).
 *
 * The endpoint answers with the API-wide `{ data: T }` success envelope (CLAUDE.md "Response
 * shape"; `openapi.yaml` `/signalr/negotiate`). This client used to destructure `url` and
 * `accessToken` off the ENVELOPE, so both were `undefined` — real-time had never once connected
 * in any environment, and the only symptom was the "no live updates" banner.
 *
 * Returns null rather than passing a partial result on, so a malformed body degrades to polling
 * through the same path as a 503 instead of throwing inside the connection builder.
 */
function parseNegotiate(body: unknown): NegotiateResponse | null {
  if (typeof body !== 'object' || body === null) return null;

  // Narrowing an `unknown` body field by field; each assertion only widens to an optional
  // property that is then checked with typeof, so nothing is trusted from the wire.
  const data: unknown = (body as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return null;

  const { url, accessToken } = data as Partial<NegotiateResponse>;
  if (typeof url !== 'string' || typeof accessToken !== 'string') return null;

  return { url, accessToken };
}

/**
 * Ask our backend for a connection URL and a freshly minted access token.
 *
 * Shared by the initial connect and by `accessTokenFactory`, so a reconnect mints a NEW token
 * instead of replaying the one captured at sign-in. Auth is the HttpOnly cookie
 * (`credentials: 'include'`) — the localStorage/Bearer token was retired in Phase 10.
 */
async function fetchNegotiate(): Promise<NegotiateResponse | null> {
  const response = await fetch(SIGNALR_NEGOTIATE_URL, { credentials: 'include' });

  if (!response.ok) {
    // 503 = AZURE_SIGNALR_CONNECTION_STRING intentionally unset (local dev). ADR-0009
    // documents this as the graceful-degradation path; polling covers it. Don't pollute the
    // console with a `warn` for the expected case — only warn on unexpected statuses.
    if (response.status !== 503) {
      console.warn(
        `SignalR negotiate failed (HTTP ${response.status}) — real-time updates disabled, falling back to polling`,
      );
    }
    return null;
  }

  const negotiated = parseNegotiate(await response.json());

  if (!negotiated) {
    console.warn(
      'SignalR negotiate returned an unexpected body — real-time updates disabled, falling back to polling',
    );
    return null;
  }

  return negotiated;
}

export async function connectSignalR(): Promise<HubConnection | null> {
  // If already connected, return existing connection
  if (connection?.state === 'Connected') return connection;

  // Single-flight: with the initial connect now retried (#330), a backoff timer and an
  // `online`/visibility wake-up can race each other (or React StrictMode's double-mount);
  // two overlapping connects would each build a socket and leak one. Racers adopt the
  // attempt already in flight instead.
  if (inFlight) return inFlight;

  inFlight = establishConnection();
  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

async function establishConnection(): Promise<HubConnection | null> {
  try {
    const negotiated = await fetchNegotiate();
    if (!negotiated) return null;

    // Loaded here, not at module scope, so the client stays off the first-paint critical path
    // (see the import note at the top). Deliberately AFTER the negotiate call: an environment
    // where real-time is unavailable answers 503 and degrades to polling without ever
    // downloading the library at all.
    const { HubConnectionBuilder, LogLevel } = await import('@microsoft/signalr');

    connection = new HubConnectionBuilder()
      .withUrl(negotiated.url, {
        // Re-negotiate rather than closing over `negotiated.accessToken` (#322). SignalR calls
        // this on the initial connection AND on every reconnect; the server mints the token with
        // a 1-hour expiry, so a captured constant meant any session that dropped after an hour
        // reconnected with an expired token and failed for good. Throwing on failure surfaces a
        // real cause and lets the retry policy above try again, which returning a known-stale
        // token would not.
        accessTokenFactory: async (): Promise<string> => {
          const fresh = await fetchNegotiate();
          if (!fresh)
            throw new Error('SignalR token refresh failed — negotiate did not return a token');
          return fresh.accessToken;
        },
      })
      .withAutomaticReconnect(RECONNECT_POLICY)
      .configureLogging(LogLevel.Warning)
      .build();

    await connection.start();
    return connection;
  } catch (err) {
    // The cause has to reach the console: this catch previously discarded the error entirely,
    // so a hard handshake failure and a healthy fallback looked identical from the outside.
    console.warn('SignalR connection failed — falling back to polling', err);
    connection = null;
    return null;
  }
}

export function disconnectSignalR(): void {
  if (connection) {
    void connection.stop();
    connection = null;
  }
}

export function getConnection(): HubConnection | null {
  return connection;
}
